// Stamps (issue 1034, BOARD-017; design/28 §4a): a drawing kept for reuse. One or more of a person's own marks, saved
// under a name to their stamp library, which is theirs at every table (like their pictures). A mark in a stamp is a
// drawing without its id, its author or the planning flag, its points RELATIVE to the stamp's centre. A copy on the map
// is ordinary drawings by the person who stamped it, placed in one `draw.addMany`. Pure: nothing here touches the
// network or a database; the server keeps the library (`vtt.stamp`, changelog vtt/014) and the client computes a copy.
import { DRAW_LIMITS, parseDrawing } from './drawings.js';
/** 100 stamps a person, 50 marks a stamp (design/28 §4a.6); a name is 60 characters at most. */
export const STAMP_LIMITS = { stamps: 100, marks: 50, name: 60 };
/** A relative point may lie as far either side of the centre as a map is wide. */
const SPAN = DRAW_LIMITS.max - DRAW_LIMITS.min;
const RELATIVE = { min: -SPAN, max: SPAN };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PLACEHOLDER = '00000000-0000-4000-8000-000000000000';
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/;
const half = (v) => Math.round(v * 2) / 2;
/** A drawing as a stamp's mark: its id, author and planning flag dropped. */
const markOf = (d) => {
    const { id: _id, ownerId: _owner, planning: _planning, ...rest } = d;
    return { ...rest, points: [...d.points] };
};
/** A stamp's name: text with no control character, 1 to 60 after trimming; else null. */
export function stampName(v) {
    if (typeof v !== 'string' || CONTROL_RE.test(v))
        return null;
    const s = v.trim();
    return s.length >= 1 && s.length <= STAMP_LIMITS.name ? s : null;
}
/** A stamp's marks, each read by the drawing parser (relative points): 1 to 50 of them, or null whole. */
export function parseStampMarks(v) {
    if (!Array.isArray(v) || v.length < 1 || v.length > STAMP_LIMITS.marks)
        return null;
    const out = [];
    for (const m of v) {
        if (typeof m !== 'object' || m === null)
            return null;
        const d = parseDrawing({ ...m, id: PLACEHOLDER, ownerId: '', planning: undefined }, RELATIVE);
        if (!d)
            return null;
        out.push(markOf(d));
    }
    return out;
}
export function parseStamp(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const s = v;
    if (typeof s.id !== 'string' || !UUID_RE.test(s.id))
        return null;
    const name = stampName(s.name);
    const marks = parseStampMarks(s.marks);
    return name && marks ? { id: s.id, name, marks } : null;
}
/** A drawing's points' extent. */
const extentOf = (points) => {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (let k = 0; k + 1 < points.length; k += 2) {
        const x = points[k];
        const y = points[k + 1];
        if (x < x0)
            x0 = x;
        if (x > x1)
            x1 = x;
        if (y < y0)
            y0 = y;
        if (y > y1)
            y1 = y;
    }
    return { x0, y0, x1, y1 };
};
/**
 * The marks a box drawn on the map makes into a stamp (design/28 §4a.2): every one of `meId`'s own drawings wholly or
 * partly inside it (its points' extent meets the box), oldest first, re-centred on the middle of their points; other
 * people's never. A planning drawing goes in as an ordinary mark. Empty when the box holds none of theirs.
 */
export function stampFrom(drawings, box, meId) {
    const b = { x0: Math.min(box.x0, box.x1), y0: Math.min(box.y0, box.y1), x1: Math.max(box.x0, box.x1), y1: Math.max(box.y0, box.y1) };
    const taken = drawings.filter((d) => {
        if (d.ownerId !== meId)
            return false;
        const e = extentOf(d.points);
        return e.x0 <= b.x1 && e.x1 >= b.x0 && e.y0 <= b.y1 && e.y1 >= b.y0;
    });
    if (!taken.length)
        return [];
    const all = extentOf(taken.flatMap((d) => d.points));
    const cx = half((all.x0 + all.x1) / 2);
    const cy = half((all.y0 + all.y1) / 2);
    return taken.map((d) => ({ ...markOf(d), points: d.points.map((p, k) => half(p - (k % 2 === 0 ? cx : cy))) }));
}
/**
 * A copy of a stamp centred on `at` (design/28 §4a.3): ordinary drawings by `meId`, each with a fresh id from `newId`,
 * planning when `planning` (the server refuses that from anyone but the DM). Each point is kept within `bounds` when
 * given, as the client keeps a hand-drawn mark, and always within the board's limits.
 */
export function placeStamp(stamp, at, meId, planning, newId = () => globalThis.crypto.randomUUID(), bounds) {
    const m = bounds ? Math.max(0, bounds.margin) : 0;
    const clamp = (v, k) => {
        let p = v + (k % 2 === 0 ? at.x : at.y);
        if (bounds)
            p = Math.max(-m, Math.min((k % 2 === 0 ? bounds.width : bounds.height) + m, p));
        return half(Math.max(DRAW_LIMITS.min, Math.min(DRAW_LIMITS.max, p)));
    };
    return stamp.marks.map((mk) => ({ ...mk, id: newId(), ownerId: meId, points: mk.points.map(clamp), ...(planning ? { planning: true } : {}) }));
}
//# sourceMappingURL=stamps.js.map